<template>
  <div class="profile-table-layout">
    <h4>{{ title }}</h4>
    <a-spin :spinning="isLoading" style="height: 100vh; top: 50%">
      <div v-if="items.length === 0" class="disabled">Нет информации</div>
      <div v-else ref="scrollAreaRef" class="scroll-area">
        <div
          class="profile-table-layout__items"
          v-for="item in items"
          :key="item.id"
        >
          <slot name="table-item" :item="item"></slot>
        </div>
      </div>
    </a-spin>
  </div>
</template>

<script setup>
  /**
   * Компонента для вывода табличных данных в профиле со скроллингом и автоматической до-загрузкой данных.
   * Пример использования
   *     <profile-table-layout
   *       :title="'Табличная форма'"
   *       :get-items="api.person.profilePublications"
   *       :url-params="new URLSearchParams({ asRole: USER_ROLE_PERFORMER })"
   *       entity="projects"
   *     >
   *       <template #table-item="slotProps">
   *         <item-view-component :item="slotProps.item" />
   *       </template>
   *     </profile-table-layout>
   */

  import { onBeforeUnmount, onMounted, ref } from 'vue';
  import { debounce } from 'lodash-es';

  const props = defineProps({
    title: {
      type: String,
      default: 'Заголовок',
    },
    /**
     * API метод для получения данных таблицы.
     * Этот метод в параметрах может содержать UrlSearchParams для передачи параметров запроса.
     * Ответ API должен быть в следующем виде
     * {
     *     "payload": {
     *         "entity": {
     *             "items": [
     *                 ...
     *             ],
     *             "total": 9,
     *             "pages": {
     *                 "size": 20,
     *                 "total": 1
     *             }
     *         }
     *     }
     * }
     */
    getItems: {
      type: Function,
      require: true,
    },
    /**
     * Имя сущности в ответе API, которая содержит список значений для таблицы
     */
    entity: {
      type: String,
      required: true,
    },
    urlParams: Object,
  });

  const isLoading = ref(false);
  const items = ref([]);

  const scrollAreaRef = ref(null);
  const scrollThreshold = 30;
  const PAGE_SIZE = 4;
  const totalPages = ref(0);
  const lastPage = ref(0);
  const currentPage = ref(1);

  onMounted(async () => {
    await getItems();
    scrollAreaRef.value?.addEventListener('scroll', handleScroll);
  });

  onBeforeUnmount(() => {
    scrollAreaRef.value?.removeEventListener('scroll', handleScroll);
  });

  async function getItems() {
    isLoading.value = true;
    const urlParams = props.urlParams ?? new URLSearchParams();
    urlParams.set('per-page', PAGE_SIZE);
    urlParams.set('page', currentPage.value.toString());

    try {
      const response = await props.getItems(urlParams);
      totalPages.value = response[props.entity].pages.total;
      if (lastPage.value !== currentPage.value) {
        items.value = items.value.concat(response[props.entity].items);
        lastPage.value = currentPage.value;
      }
    } catch (error) {
      console.error('error', error);
    } finally {
      isLoading.value = false;
    }
  }

  const handleScroll = debounce(async () => {
    const elementHeight = scrollAreaRef.value?.getBoundingClientRect().height;
    const scrollHeight = scrollAreaRef.value?.scrollHeight;
    const scrollBottom = scrollAreaRef.value?.scrollTop + elementHeight;
    if (scrollBottom + scrollThreshold > scrollHeight) {
      // Достигли дна области прокрутки
      if (totalPages.value === 0 || currentPage.value < totalPages.value) {
        currentPage.value++;
        await getItems();
      }
    }
  }, 70);
</script>

<style lang="scss" scoped>
  @use '@/assets/styles/_colors' as *;

  .profile-table-layout {
    padding: 20px;
    background-color: #f2f4f8;
    border-radius: 6px;

    .disabled {
      color: $color-text-secondary-grey;
      padding-left: 20px;
    }
  }

  .scroll-area {
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    max-height: 450px;
    overflow-y: auto;
  }
</style>
